import React, { useRef, useCallback, useEffect, Fragment } from "react";
import { PoemHighlight } from "@/types/highlights";
import { cn } from "@/lib/utils";
import { HighlightTooltip } from './HighlightTooltip';
import { HighlightNoteContent } from './HighlightNoteContent';
import { LineHighlightInfo } from "@/services/highlightProcessingService";
import { useTooltip } from "@/contexts/TooltipContext";
import { logger } from '@/utils/logger';

// Enhanced debug configuration for highlight troubleshooting
const DEBUG_CONFIG = {
  enabled: false,
  visualIndicators: true,
  debugStyles: {
    highlightStart: {
      borderLeft: '2px dashed red',
      paddingLeft: '2px',
      position: 'relative' as const
    },
    highlightEnd: {
      borderRight: '2px dashed red',
      paddingRight: '2px',
      position: 'relative' as const
    },
    positionLabel: {
      position: 'absolute' as const,
      top: '-14px',
      fontSize: '8px',
      lineHeight: '1',
      padding: '1px 2px',
      backgroundColor: 'rgba(255,0,0,0.2)',
      color: 'red',
      borderRadius: '2px',
      fontFamily: 'monospace',
      whiteSpace: 'nowrap',
      pointerEvents: 'none' as const,
      zIndex: 10
    }
  }
};

// Helper function to convert HSL color string to RGBA
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  let c = (1 - Math.abs(2 * l - 1)) * s;
  let x = c * (1 - Math.abs((h / 60) % 2 - 1));
  let m = l - c / 2;
  let r = 0, g = 0, b = 0;

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return [r, g, b];
}

// Helper function to convert color to rgba
function convertToRgba(color: string, opacity: number): string {
  // Handle HSL colors
  if (color.startsWith('hsl(')) {
    try {
      // Extract H, S, L values
      const match = color.match(/hsl\((\d+),\s*(\d+)%?,\s*(\d+)%?\)/);
      if (match) {
        const h = parseInt(match[1], 10);
        const s = parseInt(match[2], 10);
        const l = parseInt(match[3], 10);
        
        if (!isNaN(h) && !isNaN(s) && !isNaN(l)) {
          // Convert HSL to RGB
          const [r, g, b] = hslToRgb(h, s, l);
          // Return RGBA string
          return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
      }
    } catch (e) {
      logger.error(`Error parsing HSL color '${color}':`, e);
      // Fallback if parsing fails
      return `rgba(0, 0, 0, ${opacity})`; // Default to black with opacity
    }
  }

  // Handle hex colors
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    
    // Expand shorthand hex (e.g. #FFF to #FFFFFF)
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    try {
      // Parse the hex values to rgb
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        // Invalid hex, use a fallback approach
        return `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
      }
      
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
      // If parsing fails, use hex with opacity suffix
      return `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
    }
  }
  
  // Handle rgba colors - replace the opacity value
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/, `rgba($1,$2,$3,${opacity})`);
  }
  
  // Handle rgb colors - convert to rgba
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }
  
  // For other formats, return a semi-transparent version of the color
  return `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
}

// Interface for the HighlightedLine component
interface HighlightedLineProps {
  line: string;
  lineIndex: number;
  lineHighlights?: LineHighlightInfo[];
  showHighlights: boolean;
  activeHighlightId?: string;
  onHighlightInteraction?: (highlightId: string | null) => void;
  // forceOpacityLevel?: number; // Keep commented if not needed immediately
}

/**
 * Enhanced component to render a line with highlights, including multi-line highlight support
 * and visual connectors between lines.
 * 
 * This refactored component uses the centralized HighlightProcessingService for all highlight
 * processing logic and focuses solely on rendering the highlighted text segments.
 */
const HighlightedLine: React.FC<HighlightedLineProps> = ({ 
  line, 
  lineIndex,
  lineHighlights = [],
  showHighlights,
  activeHighlightId,
  onHighlightInteraction,
  // forceOpacityLevel = 0.2
}) => {
  // --- ADDED LOGGING --- 
  if (DEBUG_CONFIG.enabled) {
    logger.debug(`[HighlightedLine:${lineIndex}] Props received:`, { 
      lineLength: line.length,
      highlightsCount: lineHighlights.length, 
      showHighlights, 
      activeHighlightId,
      // Log sample highlight data if present
      sampleHighlight: lineHighlights.length > 0 ? JSON.stringify(lineHighlights[0], null, 2) : 'None'
    });
  }
  // --- END ADDED LOGGING --- 

  // MOVE ALL HOOKS TO THE TOP LEVEL - before any conditional returns
  const lineRef = useRef<HTMLDivElement>(null);
  const { setActiveTooltip, shouldShowTooltip } = useTooltip();
  // Replace useState with useRef to avoid re-renders from ref callbacks
  const highlightRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  
  // --- BEGIN LOGGING ---
  useEffect(() => {
    // Log only when highlights are expected and shown
    if (showHighlights && lineHighlights && lineHighlights.length > 0) {
      if (DEBUG_CONFIG.enabled) {
        logger.debug(`[HighlightedLine:${lineIndex}] Received lineHighlights:`, JSON.stringify(lineHighlights, null, 2));
        logger.debug(`[HighlightedLine:${lineIndex}] ActiveHighlightId: ${activeHighlightId}`);
      }
    }
  }, [lineHighlights, lineIndex, showHighlights, activeHighlightId]);
  // --- END LOGGING ---
  
  // More accurate highlight identification function
  const identifyHighlightMatches = useCallback((line: string, lineHighlights: LineHighlightInfo[]) => {
    // Function is already prepared to handle LineHighlightInfo objects directly
    return lineHighlights.map(lineHighlight => {
      return { 
        highlight: lineHighlight.highlight, 
        positionType: lineHighlight.positionType,
        startIndex: lineHighlight.startIndex
      };
    });
  }, []);
  
  // Handle tooltip display with delay to prevent flickering
  const handleHighlightHover = useCallback((highlight: PoemHighlight, isEntering: boolean) => {
    if (isEntering) {
      // Short delay before showing tooltip
      const timer = setTimeout(() => {
        onHighlightInteraction?.(highlight.id);
        setActiveTooltip(highlight.id);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Longer delay before hiding tooltip to reduce flickering
      const timer = setTimeout(() => {
        if (activeHighlightId === highlight.id) {
          onHighlightInteraction?.(null);
          setActiveTooltip(null);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [onHighlightInteraction, activeHighlightId, setActiveTooltip]);
  
  // Register highlight ref
  const registerHighlightRef = useCallback((id: string, element: HTMLSpanElement | null) => {
    if (element) {
      highlightRefs.current.set(id, element);
    } else {
      highlightRefs.current.delete(id);
    }
  }, []);
  
  // Handle highlight interactions
  const handleHighlightInteraction = useCallback((highlightId: string, action: 'enter'|'leave'|'click') => {
    if (onHighlightInteraction) {
      onHighlightInteraction(highlightId);
    }
    
    // Apply visual feedback
    if (action === 'enter') {
      const ref = highlightRefs.current.get(highlightId);
      if (ref) {
        ref.style.setProperty('--highlight-color', convertToRgba(
          lineHighlights.find(h => h.highlightId === highlightId)?.color || '#000000', 
          0.3
        ));
      }
    } else if (action === 'leave' && highlightId !== activeHighlightId) {
      const ref = highlightRefs.current.get(highlightId);
      if (ref) {
        ref.style.setProperty('--highlight-color', convertToRgba(
          lineHighlights.find(h => h.highlightId === highlightId)?.color || '#000000', 
          0.2
        ));
      }
    }
  }, [onHighlightInteraction, activeHighlightId, lineHighlights]);
  
  // Skip processing if highlights are disabled OR if no highlights for this line
  if (!showHighlights || lineHighlights.length === 0) {
    // console.log(`[HighlightedLine:${lineIndex}] No highlights to process for this line.`); // Logging moved to initial prop log
    // --- REVISED: Ensure this block returns the correct consistent div structure --- 
    // This path is taken when showHighlights is false. It must match the structure
    // and styling used when highlights ARE shown to avoid font/layout shifts.
    return (
       <div 
          ref={lineRef} // Keep ref if needed, though interactions are disabled
          className="highlight-line relative whitespace-pre-wrap font-mono text-sm" // Consistent classes
          data-line-index={lineIndex}
        >
          {/* Render raw line text, OR non-breaking space if line is empty */}
          {line === '' ? '\u00A0' : line}
        </div>
    );
  }
  
  // --- Prepare segments if highlights are enabled --- 
  // Group highlights by their start and end positions to generate segments
  type LineSegment = {
    text: string;
    start: number;
    end: number;
    highlights: LineHighlightInfo[];
  };
  
  // Create events for starts and ends of highlights
  const events: Array<{
    index: number; 
    type: 'start' | 'end'; 
    highlight: LineHighlightInfo;
  }> = [];
  
  // Add all highlight boundaries as events
  lineHighlights.forEach(highlight => {
    events.push({ 
      index: highlight.startIndex, 
      type: 'start', 
      highlight 
    });
    events.push({ 
      index: highlight.endIndex, 
      type: 'end', 
      highlight 
    });
  });
  
  // Sort events by index, with end events before start events at same position
  events.sort((a, b) => 
    a.index !== b.index 
      ? a.index - b.index 
      : (a.type === 'end' ? -1 : 1)
  );
  
  // Process events to create segments
  const segments: LineSegment[] = [];
  let activeHighlights: LineHighlightInfo[] = [];
  let lastIndex = 0;
  
  for (const event of events) {
    // Add segment for any text between the last event and this one
    if (event.index > lastIndex) {
      segments.push({
        text: line.slice(lastIndex, event.index),
        start: lastIndex,
        end: event.index,
        highlights: [...activeHighlights]
      });
    }
    
    // Update active highlights based on event type
    if (event.type === 'start') {
      activeHighlights.push(event.highlight);
    } else {
      activeHighlights = activeHighlights.filter(
        h => h.highlightId !== event.highlight.highlightId
      );
    }
    
    lastIndex = event.index;
  }
  
  // Add final segment if needed
  if (lastIndex < line.length) {
    segments.push({
      text: line.slice(lastIndex),
      start: lastIndex,
      end: line.length,
      highlights: [...activeHighlights]
    });
  }
  
  // Render each segment
  const renderedSegments = segments.map((segment, i) => {
    // No highlights for this segment
    if (segment.highlights.length === 0) {
      return (
        // Explicitly apply font-mono to ensure consistency
        <span key={`text-${i}`} className="plain-text font-mono">
          {segment.text}
        </span>
      );
    }
    
    // Check for overlapping highlights
    const hasMultipleHighlights = segment.highlights.length > 1;
    
    // Use the first highlight for primary rendering, but keep track of all for tooltips
    const highlight = segment.highlights[0];
    const { positionType, color, highlightId } = highlight;
    const isActive = activeHighlightId === highlightId;
    
    // Log only if DEBUG is enabled to avoid console spam
    if (DEBUG_CONFIG.enabled) {
      console.log(`[HighlightedLine:${lineIndex}] Rendering segment ${i} for highlight ${highlightId}. Color: ${color}, IsActive: ${isActive}`);
    }
    
    // --- NEW LOGIC: Separate leading whitespace --- START
    const leadingSpaceMatch = segment.text.match(/^(\s*)/);
    const leadingSpace = leadingSpaceMatch ? leadingSpaceMatch[1] : '';
    const textContent = segment.text.substring(leadingSpace.length);
    // --- NEW LOGIC: Separate leading whitespace --- END
    
    // Additional debug attributes for visual indicators
    let debugStyle: React.CSSProperties = {};
    if (DEBUG_CONFIG.enabled && DEBUG_CONFIG.visualIndicators) {
      if (positionType === 'start' || positionType === 'single') {
        debugStyle = { ...debugStyle, ...DEBUG_CONFIG.debugStyles.highlightStart };
      }
      if (positionType === 'end' || positionType === 'single') {
        debugStyle = { ...debugStyle, ...DEBUG_CONFIG.debugStyles.highlightEnd };
      }
    }
    
    // Create data object for all highlights in this segment (for tooltip cycling)
    const allHighlightsData = hasMultipleHighlights ? 
      segment.highlights.map(h => ({
        id: h.highlightId,
        color: h.color,
        note: h.highlight.note,
        displayLabel: h.highlight.display_label
      })) : [];
    
    return (
      <HighlightTooltip
        key={`highlight-${highlightId}-${i}`}
        content={
          hasMultipleHighlights ? (
            <div className="overlapping-highlights-tooltip">
              {segment.highlights.map((h, idx) => (
                <div 
                  key={h.highlightId} 
                  className={cn(
                    "overlapping-highlight-content",
                    idx !== 0 && "mt-2 pt-2 border-t border-gray-200"
                  )}
                >
                  <HighlightNoteContent 
                    highlight={h.highlight} 
                    isOverlapping={true}
                  />
                  {idx !== segment.highlights.length - 1 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {`(${idx + 1} of ${segment.highlights.length})`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <HighlightNoteContent 
              highlight={highlight.highlight} 
              isOverlapping={false}
            />
          )
        }
        position="top"
        maxWidth={320}
        delayShow={200}
      >
        <span // Outer span for Tooltip trigger, hover events, base structure
          ref={el => registerHighlightRef(highlightId, el)}
          className={cn(
            // Basic classes for structure/layout, NOT visual highlight style
            "highlight-container inline", // Use inline display
            "relative",
            isActive && "active-highlight-container" // For potential parent styling if needed
          )}
          style={{
            // NO background/border-radius here
            cursor: 'pointer',
            ...debugStyle // Apply debug style here if needed for positioning/borders
          } as React.CSSProperties}
          onMouseEnter={() => handleHighlightInteraction(highlightId, 'enter')}
          onMouseLeave={() => handleHighlightInteraction(highlightId, 'leave')}
          onClick={() => handleHighlightInteraction(highlightId, 'click')}
          tabIndex={0}
          role="button"
          aria-label={hasMultipleHighlights 
            ? `${segment.highlights.length} overlapping highlights` 
            : `Highlight: ${highlight.highlight.note || 'No note'}`}
          data-highlight-id={highlightId}
          // data-position-type={positionType} // Position type matters for inner span styling
          data-overlapping-count={segment.highlights.length} // Overlap count relevant here
        >
          {/* Render leading space normally */} 
          {leadingSpace.length > 0 && (
            <span className="whitespace-pre-wrap font-mono">{leadingSpace}</span>
          )}
          {/* Render the highlighted content part in its own styled span */} 
          {textContent.length > 0 && (
            <span
              className={cn(
                "highlighted-text-content font-mono", // Apply font-mono here too
                // Apply border-radius based on positionType HERE
                positionType === 'start' && "rounded-t-sm rounded-b-none",
                positionType === 'middle' && "rounded-none",
                positionType === 'end' && "rounded-t-none rounded-b-sm",
                positionType === 'single' && "rounded-sm"
              )}
              style={{
                // Apply background color HERE
                backgroundColor: convertToRgba(color, isActive ? 0.4 : 0.3),
                // Ensure display respects whitespace and wrapping if necessary
                display: 'inline', // Keep inline flow
                boxDecorationBreak: 'clone', 
                WebkitBoxDecorationBreak: 'clone'
              }}
              data-position-type={positionType} // Add position type for debugging/styling
            >
              {textContent}
            </span>
          )}
          {/* --- Overlap indicator logic remains relative to the outer span --- */}
          {hasMultipleHighlights && (
             <span 
               className="overlap-indicator" 
               style={{
                 position: 'absolute',
                 top: '-8px',
                 right: '-4px',
                 backgroundColor: 'white',
                 color: '#333',
                 fontWeight: 'bold',
                 fontSize: '10px',
                 lineHeight: 1,
                 padding: '2px 4px',
                 borderRadius: '8px',
                 boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                 zIndex: 2
               }}
             >
               {segment.highlights.length}
             </span>
          )}
        </span>
      </HighlightTooltip>
    );
  });
  
  // Render the line with highlighted segments
  return (
    <div 
      ref={lineRef}
      className={cn(
        "highlight-line relative text-sm",
        // Apply base styles required for layout and whitespace handling
        "whitespace-pre-wrap font-mono" // ADDED: Tailwind for mono and pre-wrap
      )}
      data-line-index={lineIndex}
      onMouseLeave={() => onHighlightInteraction?.(null)} // Clear hover on line leave
    >
      {/* If line is empty, render a non-breaking space to preserve height */}
      {line === '' ? '\u00A0' : renderedSegments}
    </div>
  );
};

export default HighlightedLine; 